import { useMemo } from 'react';
import { GetUsersInLocationsResponse, StatusType } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { AnalyticsCommonTypes } from '@frontend/api-analytics';
import { UsersQueries } from '@frontend/api-users';

type UseOfficeUsersParams = {
  isDemoLocation?: boolean;
  locationIds: string[];
};

type UseOfficeUsersResult = {
  activeOfficeUsers: AnalyticsCommonTypes.StringRecord;
  activeOfficeUsersForFilter: AnalyticsCommonTypes.StringRecord | undefined;
  isLoading: boolean;
  officeUsers: GetUsersInLocationsResponse['users'];
};

/**
 * Hook to fetch and filter office users for given location IDs.
 */
export const useOfficeUsers = ({ isDemoLocation = false, locationIds }: UseOfficeUsersParams): UseOfficeUsersResult => {
  const shouldFetchUsers = !isDemoLocation && locationIds.length > 0;

  const { data: officeUsersResponse, isLoading } = UsersQueries.useGetUsersInLocations(
    shouldFetchUsers ? locationIds : []
  );

  const officeUsers = useMemo(() => {
    return (officeUsersResponse as GetUsersInLocationsResponse)?.users || [];
  }, [officeUsersResponse]);

  const activeOfficeUsers = useMemo(() => {
    return officeUsers
      .filter(({ status }) => !status || status === StatusType.ACTIVE)
      .reduce((acc, { firstName = '', lastName = '', userId = '' }) => {
        if (userId) {
          acc[userId] = `${firstName} ${lastName}`.trim();
        }
        return acc;
      }, {} as AnalyticsCommonTypes.StringRecord);
  }, [officeUsers]);

  const activeOfficeUsersForFilter = useMemo(() => {
    const userCount = Object.keys(activeOfficeUsers).length;
    return userCount > 1 ? activeOfficeUsers : undefined;
  }, [activeOfficeUsers]);

  return {
    activeOfficeUsers,
    activeOfficeUsersForFilter,
    isLoading,
    officeUsers,
  };
};
